import { useState, useCallback, useEffect } from "react";
import { useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { listDayCenterPatientRecords } from "../../../api/server";
import {
  setPage,
  setActivePatient,
  updateRecords,
} from "../dayCenterPatientSlice";
import moment from "moment";

const useDayCenterPatientData = (
  record,
  activeDate,
  scopePrefix,
  selectedOrder
) => {
  const [changes, setChanges] = useState({});
  const [effectiveOrders, setEffectiveOrders] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const dispatch = useDispatch();
  const dayCenterPatientPage = useSelector(
    (state) => state.dayCenterPatient.page
  );
  const activePatient = useSelector(
    (state) => state.dayCenterPatient.activePatient
  );

  useEffect(() => {
    dispatch(setActivePatient(record.patient));
  }, [dispatch, record]);

  const { isLoading, data, refetch } = useQuery(
    [dayCenterPatientPage, activeDate, activePatient],
    async () => {
      const selectedMonth = moment(activeDate);
      const from = selectedMonth.startOf("month").format();
      const to = selectedMonth.endOf("month").format();
      const scopePrefix = `branches/${activePatient?.branch}/`;
      const result = await listDayCenterPatientRecords({
        ...dayCenterPatientPage,
        patientId: activePatient?.id,
        scopePrefix,
        query: {
          from,
          to,
        },
      });
      dispatch(
        setPage({
          ...dayCenterPatientPage,
          items: result.data.events,
          total: result.data.total,
        })
      );
      setEffectiveOrders(result?.data?.effective_orders);
      return result?.data?.events;
    },
    {
      keepPreviousData: true,
      staleTime: 0,
      enabled: activePatient?.id !== undefined,
      refetchOnWindowFocus: false, // Disable refetching on window focus
    }
  );

  useEffect(() => {
    const filteredEvents =
      data?.filter((item) => item.order_id === +selectedOrder) || [];

    const selectedMonth = moment(activeDate);
    // Generate an array of dates for the selected month
    const daysInMonth = selectedMonth.daysInMonth();
    const allDates = Array.from({ length: daysInMonth }, (_, index) => {
      const date = selectedMonth.clone().date(index + 1);
      return date;
    });
    // Merge the fetched events with the missing dates
    const mergedEvents = allDates.map((date) => {
      const fetchedEvent = filteredEvents?.find((item) =>
        moment(item?.date).isSame(date, "day")
      );

      if (fetchedEvent) {
        return {
          ...fetchedEvent,
          event_id: fetchedEvent?.event ? fetchedEvent?.event_id : undefined,
          day_center_approved_info:
            !fetchedEvent?.day_center_approved_info && fetchedEvent?.event_id
              ? 1
              : fetchedEvent?.day_center_approved_info === null
              ? 4
              : fetchedEvent?.day_center_approved_info,
          present: fetchedEvent?.event_id !== undefined,
        };
      } else {
        return {
          day_center_approved_info: 4,
          date: date.format(),
          patient_id: activePatient?.id,
          event_id: undefined,
          comment: "",
          present: false,
          approved: false,
          order_id: +selectedOrder,
        };
      }
    });

    // Set the initial state of the events in the changes object using date as the key
    const initialChanges = mergedEvents.reduce((acc, item) => {
      const dayOfMonth = moment(item.date).date();

      acc[dayOfMonth] = {
        ...item,
      };
      return acc;
    }, {});
    setChanges(initialChanges);
    setFilteredData(selectedOrder ? mergedEvents : []);
  }, [selectedOrder, data]);

  const handleChanges = useCallback((dayOfMonth, field, value) => {
    setChanges((prevChanges) => {
      const updatedChanges = {
        ...prevChanges,
        [dayOfMonth]: {
          ...prevChanges[dayOfMonth],
          [field]: value,
        },
      };
      return updatedChanges;
    });
  }, []);

  const updateServer = useCallback(async () => {
    try {
      const today = moment().format("YYYY-MM-DD");
      const filteredChanges = Object.values(changes).filter((change) => {
        const originalEvent = data.find((event) =>
          moment(event.date).isSame(change.date, "day")
        );
        return (
          moment(change.date).isSameOrBefore(today, "day") &&
          (change?.day_center_approved_info !==
            originalEvent?.day_center_approved_info ||
            change.comment !== originalEvent?.comment ||
            change.day_center_half_day !== originalEvent?.day_center_half_day ||
            change.present !== change.approved)
        );
      });
      const events = filteredChanges.map((change) => ({
        date: change.date || activeDate,
        patient_id: change.patient_id,
        day_center_approved_info:
          change.day_center_approved_info === 4
            ? null
            : change.day_center_approved_info,
        event_id: change.event_id,
        comment: change.comment,
        day_center_half_day: change.day_center_half_day,
        approved: change.day_center_approved_info !== 4,
        order_id: change.order_id,
      }));
      await dispatch(
        updateRecords({ events, scopePrefix, patientId: record.patient_id })
      );
      refetch();
    } catch (error) {
      console.error("Error updating patients:", error);
    }
  }, [
    changes,
    dispatch,
    scopePrefix,
    record.patient_id,
    refetch,
    activeDate,
    data,
  ]);

  const onChange = useCallback(
    ({ current, pageSize }, filters, { field, order }, extra) => {
      const { textFilter } = dayCenterPatientPage?.filter || {};
      const pageNumber = extra["action"] === "paginate" ? current : 1;

      const page = {
        current: pageNumber,
        pageSize,
        filter: {
          ...filters,
          freeText: textFilter,
        },
        order: {
          field,
          order,
        },
      };

      dispatch(setPage(page));
    },
    [dayCenterPatientPage?.filter, dispatch]
  );

  return {
    changes,
    isLoading,
    data,
    handleChanges,
    updateServer,
    onChange,
    effectiveOrders,
    filteredData,
  };
};

export default useDayCenterPatientData;
